import { FC, useEffect } from 'react';
import { LoadingBackdrop } from '../components/loading.backdrop.tsx';
import * as yup from 'yup';
import { Paths } from '../constants/paths.ts';
import { Form, Formik } from 'formik';
import { Divider, Stack, Typography } from '@mui/material';
import { Question } from '../components/question/question.tsx';
import { SubmitButton } from '../components/forms/submit.button.tsx';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../contexts/snackbar.context.tsx';
import { useCreateWeeklyLogMutation, useWeeklyLogQuery } from '../graphql/generated/operations/weeklyLog.ts';
import { trackEvent } from '../utils/analytics.ts';

export const WeeklyLogPage: FC = () => {
  const { data, loading } = useWeeklyLogQuery({
    variables: {
      offset: Math.floor(new Date().getTimezoneOffset() / 60) // Timezone offset in hours
    }
  });
  const [submitWeeklyLog, { data: weeklyLog, loading: submitting }] = useCreateWeeklyLogMutation();
  const navigate = useNavigate();
  const { push } = useSnackbar();

  useEffect(() => {
    if (data?.needToAnswerWeeklyLog === false) {
      trackEvent('weekly_log_already_submitted');
      push({ type: 'info', message: 'Weekly log already submitted' });
      navigate(Paths.HOME);
    }
  }, [data]);

  useEffect(() => {
    if (weeklyLog) {
      trackEvent('weekly_log_success');
      push({ type: 'success', message: 'Weekly log submitted' });
      navigate(Paths.REPLAN_ACTIVITES, { state: { fromWeeklyLog: true } });
    }
  }, [weeklyLog]);

  if (loading || submitting || !data) {
    return <LoadingBackdrop />;
  }

  const questions = [...(data?.questions || []), ...(data?.motivation || [])];

  return (
    <Formik
      validationSchema={yup.object(
        questions.reduce((acc: any, question) => {
          acc[question._id] = yup.string().required('Required');
          return acc;
        }, {})
      )}
      initialValues={{}}
      onSubmit={(values: any) => {
        trackEvent('weekly_log_submit');
        submitWeeklyLog({
          variables: {
            input: {
              offset: Math.floor(new Date().getTimezoneOffset() / 60), // Timezone offset in hours
              questions:
                Object.keys(values).map((questionID: string) => ({
                  questionID,
                  answer: values[questionID].toString()
                })) || []
            }
          }
        });
      }}
    >
      <Form>
        {questions.map((question, index) => (
          <Stack key={question._id} spacing={2}>
            <Question question={question} />
            {index !== data?.questions.length && <Divider />}
          </Stack>
        ))}
        {questions.length === 0 && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography>No questions available for this weekly log.</Typography>
          </Stack>
        )}
        <SubmitButton>Next</SubmitButton>
      </Form>
    </Formik>
  );
};
